import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { resolveUserContext } from '../lib/userContext';
import PageArrival from '../components/PageArrival';
import { Briefcase as A1, MapPin as A2, Calendar as A3, FileText as A4, Users as A5, Star as A6 } from 'lucide-react';

import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, MapPin, GraduationCap, Briefcase, Building2, DollarSign, Calendar,
  ExternalLink, Bookmark, BookmarkCheck, Wifi, Users, Sparkles, AlertCircle, Rocket
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PatriciaPageGuide from '../components/PatriciaPageGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPERIENCE_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'internship', label: 'Internship' },
  { value: 'externship', label: 'Externship' },
  { value: 'apprenticeship', label: 'Apprenticeship' },
  { value: 'fellowship', label: 'Fellowship' },
  { value: 'research_program', label: 'Research Program' },
  { value: 'summer_program', label: 'Summer Program' },
  { value: 'leadership_program', label: 'Leadership Program' },
  { value: 'volunteer', label: 'Volunteer Experience' },
  { value: 'certification', label: 'Certification' },
  { value: 'career_competition', label: 'Career Competition' },
];
const EDUCATION_LEVELS = [
  'High School Student', 'Recent High School Graduate', 'Community College Student',
  'Four-Year College Student', 'Graduate Student', 'Adult Learner', 'Not Currently Enrolled',
];
const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const TYPE_META = {
  internship: { color: 'text-cyan-300', bg: 'bg-cyan-500/15 border-cyan-500/30', icon: Briefcase },
  externship: { color: 'text-purple-300', bg: 'bg-purple-500/15 border-purple-500/30', icon: Users },
  apprenticeship: { color: 'text-amber-300', bg: 'bg-amber-500/15 border-amber-500/30', icon: GraduationCap },
};
const REMOTE_META = {
  remote: { label: 'Remote', icon: Wifi }, hybrid: { label: 'Hybrid', icon: Wifi }, in_person: { label: 'In Person', icon: MapPin },
};
const LABEL_STYLE = {
  'Current Listing': 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  'FuturePath Listing': 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  'Confirm Availability': 'bg-amber-500/15 text-amber-300 border-amber-500/30',
};

function ExperienceCard({ exp, saved, onSave, onUnsave }) {
  const meta = TYPE_META[exp.experience_type] || TYPE_META.internship;
  const TypeIcon = meta.icon;
  const remote = REMOTE_META[exp.remote_status] || REMOTE_META.in_person;
  const RemoteIcon = remote.icon;
  const loc = [exp.city, exp.state].filter(Boolean).join(', ') || (exp.remote_status === 'remote' ? 'Remote' : 'Nationwide');
  const deadline = exp.application_deadline
    ? new Date(exp.application_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Rolling';

  return (
    <motion.div
      layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#0b2440]/80 via-slate-900/70 to-[#08203A]/80 backdrop-blur-xl p-5 hover:border-cyan-400/30 transition-colors group"
      data-testid={`experience-card-${exp.id}`}
    >
      <div className="pointer-events-none absolute -top-16 -right-12 w-40 h-40 rounded-full bg-cyan-400/5 blur-3xl group-hover:bg-cyan-400/10 transition" />
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border ${meta.bg} ${meta.color}`}>
            <TypeIcon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-white font-bold text-lg leading-tight">{exp.title}</h3>
            <p className="text-slate-300 text-sm flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {exp.organization}</p>
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border whitespace-nowrap ${LABEL_STYLE[exp.label] || LABEL_STYLE['FuturePath Listing']}`} data-testid="experience-label">
          {exp.label}
        </span>
      </div>

      {exp.description && <p className="text-slate-300 text-sm mt-3 line-clamp-2">{exp.description}</p>}

      <div className="flex flex-wrap gap-2 mt-4 text-xs">
        <span className={`px-2.5 py-1 rounded-full border capitalize ${meta.bg} ${meta.color}`}>{exp.experience_type}</span>
        <span className="px-2.5 py-1 rounded-full bg-slate-800/70 text-slate-200 flex items-center gap-1"><RemoteIcon className="w-3 h-3" /> {remote.label}</span>
        <span className="px-2.5 py-1 rounded-full bg-slate-800/70 text-slate-200 flex items-center gap-1"><MapPin className="w-3 h-3" /> {loc}</span>
        {exp.paid === true && <span className="px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Paid</span>}
        {exp.paid === false && <span className="px-2.5 py-1 rounded-full bg-slate-800/70 text-slate-300">Unpaid</span>}
        <span className="px-2.5 py-1 rounded-full bg-slate-800/70 text-slate-200 flex items-center gap-1"><Calendar className="w-3 h-3" /> Due {deadline}</span>
      </div>

      <div className="flex items-center gap-2 mt-4">
        <a href={exp.source_url} target="_blank" rel="noopener noreferrer" className="flex-1">
          <Button className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-90 text-white font-semibold rounded-xl" data-testid="experience-apply-btn">
            Apply / Learn More <ExternalLink className="w-4 h-4 ml-1.5" />
          </Button>
        </a>
        {saved ? (
          <Button variant="outline" onClick={() => onUnsave(exp.id)} className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 rounded-xl" data-testid="experience-unsave-btn">
            <BookmarkCheck className="w-4 h-4" />
          </Button>
        ) : (
          <Button variant="outline" onClick={() => onSave(exp)} className="border-slate-700 text-slate-300 hover:bg-slate-800 rounded-xl" data-testid="experience-save-btn">
            <Bookmark className="w-4 h-4" />
          </Button>
        )}
      </div>
    </motion.div>
  );
}

export default function ExperiencesPage() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader, user } = useAuth();
  const ctx = resolveUserContext(user);
  const [filters, setFilters] = useState({
    experience_type: 'all', state: ctx.stateCode || '', education_level: ctx.internshipEducationLevel || 'High School Student',
    keyword: ctx.careerTarget || '', city: '', remote_status: 'all', paid_filter: 'all',
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());
  const [tab, setTab] = useState('search');
  const [savedList, setSavedList] = useState([]);
  const [liveAvailable, setLiveAvailable] = useState(false);

  useEffect(() => {
    // Pre-fill from the profile, but NEVER let it downgrade a college student's
    // education level to a high-school default (persona is the source of truth).
    axios.get(`${API}/experiences/prefill`, getAuthHeader()).then((r) => {
      const incoming = Object.fromEntries(Object.entries(r.data).filter(([, v]) => v));
      if (ctx.isCollege || ctx.isAdult) delete incoming.education_level;
      setFilters((p) => ({ ...p, ...incoming }));
    }).catch(() => {});
    loadSaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSaved = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/experiences/saved`, getAuthHeader());
      setSavedList(r.data.saved || []);
      setSavedIds(new Set((r.data.saved || []).map((s) => s.experience_id)));
    } catch { /* ignore */ }
  }, [getAuthHeader]);

  const runSearch = async () => {
    setLoading(true); setSearched(true); setTab('search');
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const r = await axios.get(`${API}/experiences/search?${params.toString()}`, getAuthHeader());
      setResults(r.data.results || []);
      setLiveAvailable(r.data.live_search_available);
    } catch {
      toast.error('Search hit a snag. Please try again.');
    } finally { setLoading(false); }
  };

  const save = async (exp) => {
    setSavedIds((p) => new Set(p).add(exp.id));
    try {
      await axios.post(`${API}/experiences/save`, { experience: exp }, getAuthHeader());
      toast.success('Saved to your FuturePath profile');
      loadSaved();
    } catch { toast.error('Could not save. Try again.'); }
  };
  const unsave = async (id) => {
    setSavedIds((p) => { const n = new Set(p); n.delete(id); return n; });
    try { await axios.delete(`${API}/experiences/saved/${id}`, getAuthHeader()); loadSaved(); }
    catch { toast.error('Could not remove.'); }
  };

  const set = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="internships-arrival"
        eyebrow="Internships & Experience"
        title="Experience is the thing they all ask for."
        subtitle="The catch-22 of every first job, you need experience to get experience. This is how you get it: internships, apprenticeships, volunteering, projects and programmes that count on a résumé."
        accent="#22d3ee"
        second="#a78bfa"
        ctaLabel="Find experience"
        narration={"This is Internships and Experience. Every employer asks for experience, and nobody explains how to get the first one. So here are the real routes: paid internships, apprenticeships, co-ops, volunteering that counts, and self-run projects that prove more than a certificate does. You will see what each one asks for, when applications open, and how to turn any of it into evidence on your record."}
        facts={[
          { icon: A1, head: "Real openings", sub: "Internships, apprenticeships and co-ops" },
          { icon: A3, head: "Timed properly", sub: "When applications actually open for each cycle" },
          { icon: A6, head: "Becomes evidence", sub: "Anything you finish lands in your skill record" }
        ]}
        modules={[
          { icon: A1, label: "Internships", line: "Paid where possible, with what they expect of you", hue: "#22d3ee" },
          { icon: A2, label: "Near you", line: "Filtered to where you are, or where you would move", hue: "#34d399" },
          { icon: A4, label: "Application help", line: "Résumé, cover note and follow-up that gets replies", hue: "#fbbf24" },
          { icon: A5, label: "Volunteering that counts", line: "Service that builds a real skill, not just hours", hue: "#a78bfa" },
          { icon: A6, label: "Build your own", line: "Projects that prove the skill when nobody will hire you yet", hue: "#f472b6" },
          { icon: A3, label: "Deadlines", line: "Saved opportunities become dated steps in your plan", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="mb-4"><PatriciaPageGuide pageId="experiences" /></div>
      <div className="max-w-6xl mx-auto pb-12">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl border border-cyan-400/25 bg-gradient-to-br from-[#0b2440] via-[#0a2036] to-[#08203A] p-6 sm:p-8 mb-6 drop-shadow-[0_0_36px_rgba(23,204,244,0.15)]"
        >
          <div className="pointer-events-none absolute -top-20 -right-10 w-72 h-72 rounded-full bg-cyan-400/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-10 w-72 h-72 rounded-full bg-blue-600/10 blur-3xl" />
          <div className="relative flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-400/30 to-blue-600/30 ring-1 ring-cyan-400/60 flex items-center justify-center text-cyan-200 drop-shadow-[0_0_16px_rgba(23,204,244,0.5)]">
              <Rocket className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white font-outfit">Internships & Career Experiences</h1>
              <p className="text-slate-300 mt-1 text-sm sm:text-base">Discover real internships, externships, and apprenticeships matched to your goals, in under a minute.</p>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5">
          <button onClick={() => setTab('search')} className={`px-4 py-2 rounded-full text-sm font-semibold transition ${tab === 'search' ? 'bg-cyan-500 text-slate-900' : 'bg-slate-800/60 text-slate-300 hover:bg-slate-800'}`} data-testid="tab-search">
            Search
          </button>
          <button onClick={() => { setTab('saved'); loadSaved(); }} className={`px-4 py-2 rounded-full text-sm font-semibold transition flex items-center gap-1.5 ${tab === 'saved' ? 'bg-cyan-500 text-slate-900' : 'bg-slate-800/60 text-slate-300 hover:bg-slate-800'}`} data-testid="tab-saved">
            <BookmarkCheck className="w-4 h-4" /> Saved ({savedList.length})
          </button>
        </div>

        {tab === 'search' && (
          <>
            {/* Search form */}
            <GlassCard className="mb-6" data-testid="experience-search-form">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-slate-300 mb-1 block">Experience Type</label>
                  <Select value={filters.experience_type} onValueChange={(v) => set('experience_type', v)}>
                    <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{EXPERIENCE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-300 mb-1 block">State</label>
                  <Select value={filters.state || 'any'} onValueChange={(v) => set('state', v === 'any' ? '' : v)}>
                    <SelectTrigger data-testid="filter-state"><SelectValue placeholder="Any state" /></SelectTrigger>
                    <SelectContent><SelectItem value="any">Any state</SelectItem>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-300 mb-1 block">Education Level</label>
                  <Select value={filters.education_level} onValueChange={(v) => set('education_level', v)}>
                    <SelectTrigger data-testid="filter-education"><SelectValue /></SelectTrigger>
                    <SelectContent>{EDUCATION_LEVELS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-300 mb-1 block">Career Interest / Keyword</label>
                  <Input value={filters.keyword} onChange={(e) => set('keyword', e.target.value)} placeholder="e.g. Nursing, Tech, Trades" data-testid="filter-keyword" onKeyDown={(e) => e.key === 'Enter' && runSearch()} />
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-3 mt-3">
                <div>
                  <label className="text-xs text-slate-300 mb-1 block">City (optional)</label>
                  <Input value={filters.city} onChange={(e) => set('city', e.target.value)} placeholder="City" data-testid="filter-city" />
                </div>
                <div>
                  <label className="text-xs text-slate-300 mb-1 block">Format</label>
                  <Select value={filters.remote_status} onValueChange={(v) => set('remote_status', v)}>
                    <SelectTrigger data-testid="filter-remote"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any format</SelectItem>
                      <SelectItem value="remote">Remote</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                      <SelectItem value="in_person">In Person</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-300 mb-1 block">Compensation</label>
                  <Select value={filters.paid_filter} onValueChange={(v) => set('paid_filter', v)}>
                    <SelectTrigger data-testid="filter-paid"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Paid or Unpaid</SelectItem>
                      <SelectItem value="paid">Paid only</SelectItem>
                      <SelectItem value="unpaid">Unpaid only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button onClick={runSearch} disabled={loading} className="w-full mt-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-90 text-white font-semibold py-6 rounded-xl text-base drop-shadow-[0_0_18px_rgba(23,204,244,0.35)]" data-testid="experience-search-btn">
                {loading ? <LoadingSpinner size="sm" /> : <><Search className="w-5 h-5 mr-2" /> Search Experiences</>}
              </Button>
            </GlassCard>

            {/* Results */}
            {loading && (
              <div className="flex flex-col items-center py-16 gap-3">
                <LoadingSpinner />
                <p className="text-slate-300 text-sm animate-pulse">Searching live opportunities across the web…</p>
              </div>
            )}

            {!loading && searched && (
              <div className="flex items-center justify-between mb-4">
                <p className="text-slate-300 text-sm" data-testid="results-count">
                  <span className="text-white font-semibold">{results.length}</span> experience{results.length !== 1 ? 's' : ''} found
                </p>
                {liveAvailable ? (
                  <span className="text-xs text-emerald-400 flex items-center gap-1" data-testid="live-badge"><Sparkles className="w-3 h-3" /> Live results</span>
                ) : (
                  <span className="text-xs text-slate-300 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Current opportunities</span>
                )}
              </div>
            )}

            {!loading && searched && results.length === 0 && (
              <GlassCard className="text-center py-14" data-testid="no-results">
                <AlertCircle className="w-14 h-14 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-1">No matches yet</h3>
                <p className="text-slate-300 max-w-md mx-auto text-sm">Try broadening your filters, widen the state, change the education level, or clear the keyword.</p>
              </GlassCard>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <AnimatePresence>
                {!loading && results.map((exp) => (
                  <ExperienceCard key={exp.id} exp={exp} saved={savedIds.has(exp.id)} onSave={save} onUnsave={unsave} />
                ))}
              </AnimatePresence>
            </div>
          </>
        )}

        {tab === 'saved' && (
          <div className="grid md:grid-cols-2 gap-4">
            {savedList.length === 0 ? (
              <GlassCard className="md:col-span-2 text-center py-14" data-testid="no-saved">
                <Bookmark className="w-14 h-14 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-1">No saved experiences yet</h3>
                <p className="text-slate-300 text-sm">Search and tap the bookmark to save experiences here. Saved items also appear in your parent's account.</p>
              </GlassCard>
            ) : (
              savedList.map((s) => (
                <ExperienceCard key={s.experience_id} exp={s.experience} saved onUnsave={unsave} onSave={save} />
              ))
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
